import { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
};

export const ThemeContextProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(false);

  // Load theme preference from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('darkMode');
    if (savedTheme !== null) {
      setDarkMode(JSON.parse(savedTheme));
    } else {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setDarkMode(prefersDark);
    }
  }, []);

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: darkMode ? '#818cf8' : '#6366f1',
        light: darkMode ? '#a5b4fc' : '#818cf8',
        dark: darkMode ? '#6366f1' : '#4f46e5',
      },
      secondary: {
        main: darkMode ? '#34d399' : '#06d6a0',
        light: darkMode ? '#6ee7b7' : '#40e0d0',
        dark: darkMode ? '#10b981' : '#059669',
      },
      success: {
        main: darkMode ? '#34d399' : '#10b981',
      },
      warning: {
        main: darkMode ? '#fbbf24' : '#f59e0b',
      },
      error: {
        main: darkMode ? '#f87171' : '#ef4444',
      },
      background: {
        default: darkMode ? '#0f172a' : '#f8fafc',
        paper: darkMode ? '#1e293b' : '#ffffff',
      },
      text: {
        primary: darkMode ? '#f1f5f9' : '#0f172a',
        secondary: darkMode ? '#94a3b8' : '#475569',
      },
      divider: darkMode ? '#334155' : '#e2e8f0',
      grey: {
        50: darkMode ? '#1e293b' : '#f8fafc',
        100: darkMode ? '#334155' : '#f1f5f9',
        200: darkMode ? '#475569' : '#e2e8f0',
        300: darkMode ? '#64748b' : '#cbd5e1',
        400: darkMode ? '#94a3b8' : '#94a3b8',
        500: darkMode ? '#cbd5e1' : '#64748b',
        600: darkMode ? '#e2e8f0' : '#475569',
        700: darkMode ? '#f1f5f9' : '#334155',
        800: darkMode ? '#f8fafc' : '#1e293b',
        900: darkMode ? '#ffffff' : '#0f172a',
      }
    },
    typography: {
      fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
      h1: { fontWeight: 800 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 700 },
      h6: { fontWeight: 600 },
    },
    shape: {
      borderRadius: 16
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(10px)',
            background: darkMode 
              ? 'rgba(30, 41, 59, 0.95)' 
              : 'rgba(255, 255, 255, 0.98)',
            border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
            boxShadow: darkMode
              ? '0 4px 20px rgba(0, 0, 0, 0.4)'
              : '0 4px 20px rgba(0, 0, 0, 0.08)',
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: darkMode ? '#1e293b' : 'rgba(248, 250, 252, 0.95)',
            borderRight: `1px solid ${darkMode ? '#334155' : 'rgba(0, 0, 0, 0.08)'}`,
            backdropFilter: 'blur(10px)',
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? '#0f172a' : 'rgba(255, 255, 255, 0.95)',
            borderBottom: `1px solid ${darkMode ? '#334155' : 'rgba(0, 0, 0, 0.08)'}`,
            backdropFilter: 'blur(10px)',
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.9)',
              },
              '&.Mui-focused': {
                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.95)',
              }
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
            '&:hover': {
              backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.9)',
            }
          }
        }
      }
    }
  });

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
